import type { DemoDataStartedPayload, DemoDataReadyPayload, DemoDataFailedPayload } from '@/realtime/types'
import { getEcho } from '@/lib/pusher'
import { rAPI } from '@app.raytd.com/store'

/**
 * Subscribe to demo data notifications via private channel events.
 * 
 * NOTE: For demo data notifications to also appear in the notification bell (database channel),
 * the backend should send both:
 * 1. Custom events (DemoDataStarted, DemoDataReady, DemoDataFailed) - for real-time updates
 * 2. Database notifications via Laravel's notification system - for persistence in notification bell
 * 
 * The backend should use Laravel's Notification facade to create database notifications
 * when demo data events occur, which will automatically broadcast via the .notification() channel.
 */
export function subscribeDemoNotifications({ userId, dispatch, onStarted, onReady, onFailed }: {
  userId: number
  dispatch: (action: any) => void
  onStarted?: (p: DemoDataStartedPayload) => void
  onReady?: (p: DemoDataReadyPayload) => void
  onFailed?: (p: DemoDataFailedPayload) => void
}) {
  const echo = getEcho()
  if (!echo) return () => {}

  const channelName = `user.${userId}`
  const channel = echo.private(channelName)
    .listen('DemoDataStarted', (e: DemoDataStartedPayload) => {
      onStarted?.(e)
      // Invalidate notifications cache in case backend also sent a database notification
      dispatch(rAPI.util.invalidateTags([{ type: 'Notifications', id: 'LIST' }]))
    })
    .listen('DemoDataReady', (e: DemoDataReadyPayload) => {
      onReady?.(e)
      dispatch(rAPI.util.invalidateTags(['Reports', 'Assets', 'Organisations']))
      // Invalidate notifications cache in case backend also sent a database notification
      dispatch(rAPI.util.invalidateTags([{ type: 'Notifications', id: 'LIST' }]))
    })
    .listen('DemoDataFailed', (e: DemoDataFailedPayload) => {
      onFailed?.(e)
      // Invalidate notifications cache in case backend also sent a database notification
      dispatch(rAPI.util.invalidateTags([{ type: 'Notifications', id: 'LIST' }]))
    })

  return () => {
    echo.leave(channelName)
  }
}

